import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Lock, LogOut, Settings, Percent, CreditCard, DollarSign, Calendar, Download, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import AdminSubNav from "../components/AdminSubNav";
import { isSuperadmin as checkSuperadmin, canManageOrg } from "../lib/permissions";
import { ResearchServicesPanel } from "../components/ResearchServicesPanel";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminSettings = ({ user, setUser }) => {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Platform settings state
  const [platformSettings, setPlatformSettings] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(false);
  
  // Editable fields
  const [feeInput, setFeeInput] = useState("30");
  const [reviewFeeInput, setReviewFeeInput] = useState("75");
  const [defaultPriceInput, setDefaultPriceInput] = useState("30");
  const [downloadLimitInput, setDownloadLimitInput] = useState("10");
  const [trialDaysInput, setTrialDaysInput] = useState("5");
  const [subscriptionPlans, setSubscriptionPlans] = useState({
    "1m": { price: 10, duration_months: 1, name: "1 Month" },
    "3m": { price: 25, duration_months: 3, name: "3 Months" },
    "6m": { price: 48, duration_months: 6, name: "6 Months" },
    "12m": { price: 70, duration_months: 12, name: "12 Months" },
  });

  const isSuperadmin = checkSuperadmin(user);
  const canViewSettings = canManageOrg(user);

  useEffect(() => {
    if (canViewSettings) {
      fetchPlatformSettings();
    }
  }, [canViewSettings]);

  const fetchPlatformSettings = async () => {
    try {
      const token = localStorage.getItem("access_token");
      const res = await axios.get(`${API}/admin/platform-settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPlatformSettings(res.data);
      
      // Set editable fields from response
      setFeeInput(res.data.platform_fee_percentage?.toString() || "30");
      setReviewFeeInput(res.data.review_publishing_fee?.toString() || "75");
      setDefaultPriceInput(res.data.default_item_price?.toString() || "30");
      setDownloadLimitInput(res.data.free_download_limit?.toString() || "10");
      setTrialDaysInput(res.data.trial_days?.toString() || "5");
      
      if (res.data.subscription_plans) {
        setSubscriptionPlans(res.data.subscription_plans);
      }
    } catch (err) {
      console.error("Failed to load platform settings:", err);
    }
  };

  const handleUpdateSettings = async (updates) => {
    setSettingsLoading(true);
    try {
      const token = localStorage.getItem("access_token");
      const res = await axios.patch(
        `${API}/admin/platform-settings`,
        updates,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPlatformSettings(res.data);
      toast.success("Settings updated!");
    } catch (err) {
      console.error("Failed to update settings:", err);
      toast.error(err.response?.data?.detail || "Failed to update settings");
    } finally {
      setSettingsLoading(false);
    }
  };

  const handleSaveFees = () => {
    const fee = parseFloat(feeInput);
    const reviewFee = parseFloat(reviewFeeInput);
    const defaultPrice = parseFloat(defaultPriceInput);
    
    if (isNaN(fee) || fee < 0 || fee > 100) {
      toast.error("Platform fee must be between 0 and 100");
      return;
    }
    if (isNaN(reviewFee) || reviewFee < 0) {
      toast.error("Review fee must be 0 or greater");
      return;
    }
    if (isNaN(defaultPrice) || defaultPrice < 0) {
      toast.error("Default price must be 0 or greater");
      return;
    }
    
    handleUpdateSettings({
      platform_fee_percentage: fee,
      review_publishing_fee: reviewFee,
      default_item_price: defaultPrice,
    });
  };

  const handleSaveLimits = () => {
    const downloadLimit = parseInt(downloadLimitInput);
    const trialDays = parseInt(trialDaysInput);
    
    if (isNaN(downloadLimit) || downloadLimit < 0) {
      toast.error("Download limit must be 0 or greater");
      return;
    }
    if (isNaN(trialDays) || trialDays < 0) {
      toast.error("Trial days must be 0 or greater");
      return;
    }
    
    handleUpdateSettings({
      free_download_limit: downloadLimit,
      trial_days: trialDays,
    });
  };

  const handleSaveSubscriptionPlans = () => {
    // Validate all plan prices
    for (const [planId, plan] of Object.entries(subscriptionPlans)) {
      if (isNaN(plan.price) || plan.price < 0) {
        toast.error(`Invalid price for ${plan.name}`);
        return;
      }
    }
    
    handleUpdateSettings({ subscription_plans: subscriptionPlans });
  };

  const updatePlanPrice = (planId, newPrice) => {
    setSubscriptionPlans(prev => ({
      ...prev,
      [planId]: { ...prev[planId], price: parseFloat(newPrice) || 0 }
    }));
  };

  const handleToggleConnect = async (enabled) => {
    handleUpdateSettings({ stripe_connect_enabled: enabled });
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();

    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error("New passwords don't match");
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem("access_token");
      const formData = new FormData();
      formData.append("current_password", currentPassword);
      formData.append("new_password", newPassword);

      await axios.post(`${API}/auth/change-password`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });

      toast.success("Password changed successfully!");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error) {
      console.error("Change password error:", error);
      toast.error(error.response?.data?.detail || "Error changing password");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("access_token");
    localStorage.removeItem("user");
    setUser(null);
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Admin Navigation */}
      <nav className="bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-between items-center min-h-16 gap-y-2 py-2">
            <h1 className="text-xl font-semibold" style={{ fontFamily: "Fraunces, serif" }}>
              Admin Dashboard
            </h1>
            <div className="flex w-full sm:w-auto flex-wrap items-center gap-2 justify-end">
              <Button
                onClick={() => navigate("/")}
                variant="ghost"
                className="text-white hover:text-white hover:bg-slate-800"
              >
                Public Site
              </Button>
              <Button
                onClick={handleLogout}
                variant="ghost"
                size="sm"
                className="text-white hover:text-white hover:bg-slate-800"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <AdminSubNav user={user} activeTab="settings" />

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        <h2 className="text-2xl font-bold" style={{ fontFamily: "Fraunces, serif" }}>
          Settings
        </h2>

        {isSuperadmin && <ResearchServicesPanel />}

        {/* Platform Settings - Only for superadmin */}
        {isSuperadmin && platformSettings && (
          <>
            {/* Subscription Plans */}
            <div className="bg-white border border-slate-200 p-6 rounded-md">
              <div className="flex items-center mb-4">
                <CreditCard className="w-5 h-5 mr-2 text-slate-600" />
                <h3 className="text-lg font-semibold">Subscription Plans</h3>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                {Object.entries(subscriptionPlans).map(([planId, plan]) => (
                  <div key={planId} className="border border-slate-200 rounded-md p-3">
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      {plan.name}
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={plan.price}
                        onChange={(e) => updatePlanPrice(planId, e.target.value)}
                        className="pl-7"
                        data-testid={`plan-${planId}-price`}
                      />
                    </div>
                  </div>
                ))}
              </div>
              
              <Button
                onClick={handleSaveSubscriptionPlans}
                disabled={settingsLoading}
                className="bg-orange-500 hover:bg-orange-600 text-white"
                data-testid="save-subscription-plans-btn"
              >
                {settingsLoading ? "Saving..." : "Save Subscription Plans"}
              </Button>
            </div>

            {/* Fees & Pricing */}
            <div className="bg-white border border-slate-200 p-6 rounded-md">
              <div className="flex items-center mb-4">
                <DollarSign className="w-5 h-5 mr-2 text-slate-600" />
                <h3 className="text-lg font-semibold">Fees & Pricing</h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                {/* Platform Fee */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Platform Fee (%)
                  </label>
                  <p className="text-xs text-slate-500 mb-2">Revenue split for author payouts</p>
                  <div className="relative">
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      step="0.1"
                      value={feeInput}
                      onChange={(e) => setFeeInput(e.target.value)}
                      className="pr-8"
                      data-testid="platform-fee-input"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">%</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    Platform: {feeInput}% | Author: {(100 - parseFloat(feeInput || 0)).toFixed(1)}%
                  </p>
                </div>

                {/* Review & Publishing Fee */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Review & Publishing Fee
                  </label>
                  <p className="text-xs text-slate-500 mb-2">One-time article review fee</p>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={reviewFeeInput}
                      onChange={(e) => setReviewFeeInput(e.target.value)}
                      className="pl-7"
                      data-testid="review-fee-input"
                    />
                  </div>
                </div>

                {/* Default Item Price */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Default Item Price
                  </label>
                  <p className="text-xs text-slate-500 mb-2">Default price for paid downloads</p>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={defaultPriceInput}
                      onChange={(e) => setDefaultPriceInput(e.target.value)}
                      className="pl-7"
                      data-testid="default-price-input"
                    />
                  </div>
                </div>
              </div>

              <Button
                onClick={handleSaveFees}
                disabled={settingsLoading}
                className="bg-orange-500 hover:bg-orange-600 text-white"
                data-testid="save-fees-btn"
              >
                {settingsLoading ? "Saving..." : "Save Fees & Pricing"}
              </Button>
            </div>

            {/* Limits & Trial */}
            <div className="bg-white border border-slate-200 p-6 rounded-md">
              <div className="flex items-center mb-4">
                <Settings className="w-5 h-5 mr-2 text-slate-600" />
                <h3 className="text-lg font-semibold">Limits & Trial</h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                {/* Free Download Limit */}
                <div>
                  <div className="flex items-center mb-1">
                    <Download className="w-4 h-4 mr-1 text-slate-500" />
                    <label className="text-sm font-medium text-slate-700">Free Download Limit</label>
                  </div>
                  <p className="text-xs text-slate-500 mb-2">Downloads per month for free users</p>
                  <Input
                    type="number"
                    min="0"
                    value={downloadLimitInput}
                    onChange={(e) => setDownloadLimitInput(e.target.value)}
                    data-testid="download-limit-input"
                  />
                </div>

                {/* Trial Days */}
                <div>
                  <div className="flex items-center mb-1">
                    <Calendar className="w-4 h-4 mr-1 text-slate-500" />
                    <label className="text-sm font-medium text-slate-700">Trial Period (Days)</label>
                  </div>
                  <p className="text-xs text-slate-500 mb-2">Free trial duration for new users</p>
                  <Input
                    type="number"
                    min="0"
                    value={trialDaysInput}
                    onChange={(e) => setTrialDaysInput(e.target.value)}
                    data-testid="trial-days-input"
                  />
                </div>
              </div>

              <Button
                onClick={handleSaveLimits}
                disabled={settingsLoading}
                className="bg-orange-500 hover:bg-orange-600 text-white"
                data-testid="save-limits-btn"
              >
                {settingsLoading ? "Saving..." : "Save Limits"}
              </Button>
            </div>

            {/* Stripe Connect Toggle */}
            <div className="bg-white border border-slate-200 p-6 rounded-md">
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center mb-1">
                    <CreditCard className="w-5 h-5 mr-2 text-slate-600" />
                    <h3 className="text-lg font-semibold">Stripe Connect</h3>
                  </div>
                  <p className="text-sm text-slate-500">
                    Enable automatic author payouts via Stripe Connect
                  </p>
                </div>
                <Switch
                  checked={platformSettings.stripe_connect_enabled}
                  onCheckedChange={handleToggleConnect}
                  disabled={settingsLoading}
                  data-testid="stripe-connect-toggle"
                />
              </div>
            </div>

            {/* Last Updated */}
            {platformSettings.updated_at && (
              <p className="text-xs text-slate-400 text-right">
                Last updated: {new Date(platformSettings.updated_at).toLocaleString()} by {platformSettings.updated_by || "system"}
              </p>
            )}
          </>
        )}

        {/* Account Info */}
        <div className="bg-white border border-slate-200 p-6 rounded-md">
          <h3 className="text-lg font-semibold mb-4">Account Information</h3>
          <div className="space-y-2">
            <p><strong>Email:</strong> {user?.email}</p>
            <p><strong>Role:</strong> {user?.role}</p>
            <p><strong>Organization:</strong> {user?.organization || "Public"}</p>
            <p><strong>Member since:</strong> {new Date(user?.created_at).toLocaleDateString()}</p>
          </div>
        </div>

        {/* Change Password */}
        <div className="bg-white border border-slate-200 p-6 rounded-md">
          <div className="flex items-center mb-4">
            <Lock className="w-5 h-5 mr-2 text-slate-600" />
            <h3 className="text-lg font-semibold">Change Password</h3>
          </div>

          <form onSubmit={handleChangePassword} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Current Password</label>
              <Input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
                placeholder="Enter current password"
                data-testid="current-password-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">New Password</label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                placeholder="Enter new password (min 6 characters)"
                data-testid="new-password-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Confirm New Password</label>
              <Input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                placeholder="Confirm new password"
                data-testid="confirm-password-input"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="bg-orange-500 hover:bg-orange-600 text-white"
              data-testid="change-password-btn"
            >
              {loading ? "Changing..." : "Change Password"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AdminSettings;
